import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faStar, faGem, faCrown } from '@fortawesome/free-solid-svg-icons';
import client from '../lib/feathers';

const TIER_CONFIG = {
  claimed: { label: 'Claimed', color: 'bg-gray-100 text-gray-800', icon: faCheck },
  enhanced: { label: 'Enhanced', color: 'bg-blue-100 text-blue-800', icon: faStar },
  premium: { label: 'Premium', color: 'bg-purple-100 text-purple-800', icon: faGem },
  reserve: { label: 'Reserve', color: 'bg-amber-100 text-amber-800', icon: faCrown },
};

const STATUS_CONFIG = {
  active: 'bg-green-100 text-green-800',
  past_due: 'bg-yellow-100 text-yellow-800',
  canceled: 'bg-red-100 text-red-800',
  trialing: 'bg-blue-100 text-blue-800',
  incomplete: 'bg-gray-100 text-gray-600',
};

export default function ListingSubscriptionAdmin({ listing, listingId }) {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    if (!listingId) return;
    const loadEvents = async () => {
      try {
        const service = client.service('admin/subscription-event-log');
        const result = await service.find({
          query: { listingId, $sort: { createdAt: -1 }, $limit: 50 },
        });
        setEvents(result.data || []);
      } catch (err) {
        console.log('Error loading subscription events:', err);
      }
    };
    loadEvents();
  }, [listingId]);

  const tier = listing?.subscriptionTier || 'claimed';
  const tierConfig = TIER_CONFIG[tier] || TIER_CONFIG.claimed;
  const status = listing?.subscriptionStatus;
  const statusClass = STATUS_CONFIG[status] || 'bg-gray-100 text-gray-600';

  const hasSub = listing?.stripeCustomerId || listing?.stripeSubscriptionId;

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg my-5">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
        <h3 className="text-lg font-medium leading-6 text-gray-900">Business Subscription</h3>
      </div>
      <div className="px-4 py-5 sm:p-6">
        {/* Tier & Status */}
        <div className="flex items-center gap-3 mb-4">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${tierConfig.color}`}>
            <FontAwesomeIcon icon={tierConfig.icon} className="w-3.5 h-3.5" />
            {tierConfig.label}
          </span>
          {status && (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClass}`}>
              {status}
            </span>
          )}
        </div>

        {/* Subscription Details */}
        <dl className="space-y-3">
          {listing?.stripeCustomerId && (
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">Stripe Customer</dt>
              <dd className="text-sm">
                <a
                  href={`https://dashboard.stripe.com/customers/${listing.stripeCustomerId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  {listing.stripeCustomerId}
                </a>
              </dd>
            </div>
          )}
          {listing?.stripeSubscriptionId && (
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">Subscription ID</dt>
              <dd className="text-sm">
                <a
                  href={`https://dashboard.stripe.com/subscriptions/${listing.stripeSubscriptionId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  {listing.stripeSubscriptionId}
                </a>
              </dd>
            </div>
          )}
          {listing?.subscriptionCurrentPeriodEnd && (
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">Current Period End</dt>
              <dd className="text-sm text-gray-900">
                {format(new Date(listing.subscriptionCurrentPeriodEnd), 'MMM d, yyyy')}
              </dd>
            </div>
          )}
          {listing?.subscriptionCancelAtPeriodEnd && (
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">Cancel Status</dt>
              <dd className="text-sm text-red-600 font-medium">Cancels at period end</dd>
            </div>
          )}
          {!hasSub && (
            <p className="text-sm text-gray-400">No active subscription</p>
          )}
        </dl>

        {/* Event Log */}
        {events.length > 0 && (
          <div className="mt-6 border-t border-gray-200 pt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Event Log</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Event</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {events.map((evt, i) => (
                    <tr key={evt.id || i}>
                      <td className="px-3 py-2 text-xs text-gray-400 whitespace-nowrap">
                        {format(new Date(evt.createdAt), 'MMM d, h:mm a')}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-700">{evt.eventType || evt.type}</td>
                      <td className="px-3 py-2 text-xs text-gray-500">{evt.summary || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
